import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Printer, ChefHat, DollarSign, TrendingUp, Users, Clock, Thermometer, Package } from 'lucide-react';
import { calculateRecipeCostPerServing, formatPercentage } from '../../utils/calculations';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTheme } from '../../contexts/ThemeContext';
import { recipesAPI } from '../../utils/api';
import AllergenBadge from '../Allergens/AllergenBadge.jsx';

const RecipeDetails = ({ recipe, onClose }) => {
  const { t, i18n } = useTranslation();
  const { formatCurrency } = useCurrency();
  const { theme, isDark } = useTheme();
  const [categories, setCategories] = React.useState([]);
  const [fullRecipe, setFullRecipe] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [imageModalOpen, setImageModalOpen] = React.useState(false);
  const [modalImageUrl, setModalImageUrl] = React.useState('');
  const [logoUrl, setLogoUrl] = React.useState('');

  React.useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const categoriesData = await recipesAPI.getCategories();
        setCategories(categoriesData);
        
        const fullRecipeData = await recipesAPI.getById(recipe.id);
        setFullRecipe(fullRecipeData);

        // Fetch logo
        try {
          const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
          const logoResponse = await fetch(`${API_BASE_URL}/preferences/logo`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });

          if (logoResponse.ok) {
            const logoData = await logoResponse.json();
            if (logoData.success && logoData.value && logoData.value.trim() !== '') {
              setLogoUrl(logoData.value);
            }
          }
        } catch (error) {
          console.error('Error fetching logo:', error);
          // Continue without logo if fetch fails
        }
      } catch (error) {
        console.error('Error loading recipe details:', error);
        setError('Failed to load recipe details');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [recipe.id]);

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name_en : t('recipes.unnamed');
  };

  const handlePrint = async () => {
    // Fetch logo before printing to ensure we have the latest
    let currentLogoUrl = logoUrl;
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const logoResponse = await fetch(`${API_BASE_URL}/preferences/logo`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (logoResponse.ok) {
        const logoData = await logoResponse.json();
        if (logoData.success && logoData.value && logoData.value.trim() !== '') {
          currentLogoUrl = logoData.value;
        }
      }
    } catch (error) {
      console.error('Error fetching logo for print:', error);
      // Continue without logo if fetch fails
    }

    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (printWindow) {
      // Generate allergen badges HTML
      const allergensHtml = fullRecipe?.computed_allergens && fullRecipe.computed_allergens.length > 0 
        ? fullRecipe.computed_allergens.map(allergen => 
            `<span style="display: inline-block; background: #fef3c7; color: #92400e; padding: 2px 5px; border-radius: 2px; font-size: 9px; margin: 1px;">${allergen.replace('_', ' ')}</span>`
          ).join(' ')
        : '<span style="color: #6b7280;">-</span>';

      // Generate ingredients HTML
      const ingredientsHtml = fullRecipe?.ingredients && fullRecipe.ingredients.length > 0
        ? fullRecipe.ingredients.map(ingredient => {
            const isMaterial = ingredient.material_id;
            const name = isMaterial ? ingredient.material_name_en : ingredient.recipe_name_en;
            const quantity = parseFloat(ingredient.quantity || 0);
            const wastePercentage = parseFloat(ingredient.waste_percentage || 0);
            const effectiveQuantity = quantity * (1 + wastePercentage / 100);
            
            const cost = isMaterial ? 
              (ingredient.material_unit_cost || 0) * effectiveQuantity :
              (ingredient.recipe_cost_per_serving || 0) * effectiveQuantity;
            const percentage = (fullRecipe.total_cost || 0) > 0 ? ((cost / fullRecipe.total_cost) * 100) : 0;
            
            const unit = isMaterial 
              ? (ingredient.material_sub_unit || 'piece')
              : (ingredient.recipe_unit || 'serving');
            
            return `
              <div style="background: #f0f9ff; padding: 3px 5px; margin: 2px 0; border-radius: 2px; border: 1px solid #bae6fd;">
                <div style="font-weight: 600; color: #0c4a6e; font-size: 9px;">${name}</div>
                <div style="color: #0369a1; font-size: 8px;">${ingredient.quantity} ${unit} ${wastePercentage > 0 ? `(+${wastePercentage}% waste)` : ''} | Cost: ${formatCurrency(cost)} (${percentage.toFixed(1)}%)</div>
              </div>`;
          }).join('')
        : '<div style="color: #6b7280; font-style: italic; font-size: 9px;">No ingredients found</div>';

      const totalCost = fullRecipe?.total_cost || 0;
      const costPerServing = calculateRecipeCostPerServing(fullRecipe);

      // Create the print document
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Dish Details - ${fullRecipe?.name_en}</title>
          <style>
            @page { 
              margin: 0.25in; 
              size: A4; 
            }
            
            * {
              box-sizing: border-box;
            }
            
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 0;
              background: white;
              color: #000;
              font-size: 10px;
              line-height: 1.3;
            }
            
            .header {
              text-align: center;
              margin-bottom: 8px;
              padding-bottom: 6px;
              border-bottom: 1px solid #000;
            }
            
            .header img {
              max-height: 40px !important;
              max-width: 150px !important;
              margin-bottom: 4px !important;
            }
            
            .title {
              font-size: 18px;
              font-weight: bold;
              margin: 0;
              margin-bottom: 2px;
              line-height: 1.2;
            }
            
            .subtitle {
              font-size: 12px;
              color: #666;
              margin: 0;
              margin-bottom: 2px;
              line-height: 1.2;
            }
            
            .date {
              font-size: 9px;
              color: #888;
              margin-top: 2px;
            }
            
            .section {
              margin-bottom: 8px;
              page-break-inside: avoid;
            }
            
            .section-title {
              font-size: 11px;
              font-weight: bold;
              margin-bottom: 5px;
              padding: 3px 0;
              border-bottom: 1px solid #ccc;
            }
            
            .info-card {
              background: #f8f9fa;
              border: 1px solid #dee2e6;
              padding: 5px;
              border-radius: 3px;
              margin-bottom: 5px;
            }
            
            .info-label {
              font-size: 9px;
              font-weight: 600;
              color: #495057;
              margin-bottom: 3px;
              text-transform: uppercase;
            }
            
            .info-item {
              display: flex;
              justify-content: space-between;
              margin-bottom: 2px;
              font-size: 9px;
            }
            
            .info-item strong {
              font-weight: 600;
              margin-right: 4px;
            }
            
            .cost-card {
              background: #e8f5e8;
              border: 1px solid #c3e6c3;
              padding: 5px;
              border-radius: 3px;
              text-align: center;
            }
            
            .cost-label {
              font-size: 9px;
              color: #2d5a2d;
              font-weight: 600;
              margin-bottom: 2px;
            }
            
            .cost-value {
              font-size: 12px;
              font-weight: bold;
              color: #047857;
            }
            
            .spec-card {
              background: #eff6ff;
              border: 1px solid #bfdbfe;
              padding: 5px;
              border-radius: 3px;
              text-align: center;
            }
            
            .spec-label {
              font-size: 9px;
              color: #1d4ed8;
              font-weight: 600;
              margin-bottom: 2px;
            }
            
            .spec-value {
              font-size: 11px;
              font-weight: bold;
              color: #1e40af;
            }
            
            .ingredients-details {
              max-height: none !important;
              overflow-y: visible !important;
              overflow: visible !important;
            }
            
            .ingredients-details > div {
              margin-bottom: 3px !important;
              padding: 3px 5px !important;
              font-size: 9px !important;
            }
            
            .two-column {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 6px;
            }
            
            .grid-3 {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 4px;
            }
            
            .grid-4 {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr 1fr;
              gap: 4px;
            }
            
            .full-width {
              grid-column: 1 / -1;
            }
            
            .highlight {
              background: #fef3c7;
              padding: 1px 3px;
              border-radius: 2px;
            }
            
            /* Compact table styles */
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 9px;
              margin: 4px 0;
            }
            
            table th, table td {
              padding: 3px 4px;
              border: 1px solid #dee2e6;
              text-align: left;
            }
            
            table th {
              background: #f3f4f6;
              font-weight: 600;
              font-size: 9px;
            }
          </style>
        </head>
        <body>
          <!-- Header -->
          <div class="header">
            ${currentLogoUrl ? `<div style="text-align: center; margin-bottom: 4px;"><img src="${currentLogoUrl}" alt="Logo" style="max-height: 40px; max-width: 150px; object-fit: contain;" /></div>` : ''}
            <h1 class="title">${fullRecipe?.name_en}</h1>
            <div class="subtitle">${fullRecipe?.name_ar}</div>
            <div class="date">Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</div>
          </div>

          <!-- Recipe Overview -->
          <div class="section">
            <div class="section-title">🍳 Recipe Overview</div>
            
            <div class="two-column">
              <div class="info-card">
                <div class="info-label">Basic Information</div>
                <div style="margin-top: 3px;">
                  <div class="info-item">
                    <span><strong>English:</strong></span>
                    <span>${fullRecipe?.name_en}</span>
                  </div>
                  <div class="info-item">
                    <span><strong>Arabic:</strong></span>
                    <span>${fullRecipe?.name_ar}</span>
                  </div>
                  <div class="info-item">
                    <span><strong>Category:</strong></span>
                    <span>${getCategoryName(fullRecipe?.category_id)}</span>
                  </div>
                  <div class="info-item">
                    <span><strong>Servings:</strong></span>
                    <span>${fullRecipe?.servings || 'N/A'}</span>
                  </div>
                  <div class="info-item">
                    <span><strong>Portion Size:</strong></span>
                    <span>${fullRecipe?.portion_size || 'N/A'} ${fullRecipe?.unit || ''}</span>
                  </div>
                  <div class="info-item">
                    <span><strong>Holding Temperature:</strong></span>
                    <span>${fullRecipe?.holding_temperature ? `${fullRecipe.holding_temperature}°C` : '-'}</span>
                  </div>
                </div>
              </div>
              
              <div class="info-card">
                <div class="info-label">Nutritional Information</div>
                <div style="margin-top: 3px;">
                  <div style="margin-top: 3px;">
                    <div class="info-label" style="font-size: 9px; margin-bottom: 2px;">Allergens</div>
                    <div style="margin-top: 2px;">${allergensHtml}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>


          <!-- Cost Breakdown -->
          <div class="section">
            <div class="section-title">💰 Cost Breakdown</div>
            <div class="grid-3">
              <div class="cost-card">
                <div class="cost-label">Total Cost</div>
                <div class="cost-value">${formatCurrency(totalCost)}</div>
              </div>
              <div class="cost-card">
                <div class="cost-label">Cost per Serving</div>
                <div class="cost-value">${formatCurrency(costPerServing)}</div>
              </div>
              <div class="cost-card">
                <div class="cost-label">Total Servings Cost</div>
                <div class="cost-value">${formatCurrency(costPerServing * (fullRecipe?.servings || 0))}</div>
              </div>
            </div>
          </div>

          <!-- Ingredients -->
          <div class="section">
            <div class="section-title">🥘 Ingredients</div>
            <div class="info-card">
              <div class="info-label">Ingredient Details</div>
              <div class="ingredients-details" style="margin-top: 3px;">
                ${ingredientsHtml}
              </div>
            </div>
          </div>

          <!-- Procedure -->
          <div class="section">
            <div class="section-title">📋 Procedure</div>
            <div class="info-card">
              <div class="info-label">Detailed Procedure</div>
              <div style="margin-top: 3px;">
                ${fullRecipe?.procedures && fullRecipe.procedures.length > 0 ? 
                  fullRecipe.procedures.map(procedure => 
                    `<div style="margin-bottom: 4px; padding-left: 5px; border-left: 1px solid #3b82f6;">
                      <div style="display: flex; align-items: flex-start; gap: 5px;">
                        <span style="display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; background: #3b82f6; color: white; font-size: 8px; font-weight: bold; border-radius: 50%; flex-shrink: 0;">${procedure.step_number}</span>
                        <div style="flex: 1;">
                          <p style="font-size: 9px; line-height: 1.3; color: #000; margin: 0;">${procedure.instruction_en}</p>
                          ${procedure.image_url ? `<img src="${procedure.image_url}" alt="Step ${procedure.step_number}" style="width: 45px; height: 45px; object-fit: cover; border-radius: 2px; border: 1px solid #93c5fd; margin-top: 2px;">` : ''}
                        </div>
                      </div>
                    </div>`
                  ).join('')
                  : `<p style="font-size: 9px; line-height: 1.3; color: #000; white-space: pre-wrap;">${fullRecipe?.instructions_en || fullRecipe?.instructions || fullRecipe?.procedure || 'No procedure available for this recipe.'}</p>`
                }
              </div>
            </div>
          </div>
        </body>
        </html>
      `);
      
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };
    } else {
      // Fallback to regular print if new window fails
    window.print();
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content max-w-4xl">
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Loading recipe details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay">
        <div className="modal-content max-w-4xl">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-card-foreground">Error</h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-card-foreground">
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="text-center py-8">
            <p className="text-destructive">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!fullRecipe) {
    return (
      <div className="modal-overlay">
        <div className="modal-content max-w-4xl">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-card-foreground">Recipe Not Found</h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-card-foreground">
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="text-center py-8">
            <p className="text-muted-foreground">Recipe details could not be loaded.</p>
          </div>
        </div>
      </div>
    );
  }

  const totalCost = fullRecipe.total_cost || 0;
  const costPerServing = calculateRecipeCostPerServing(fullRecipe);

  // Debug: Log the fullRecipe object to see available fields
  console.log('Full recipe data:', fullRecipe);
  console.log('Available fields:', Object.keys(fullRecipe));
  console.log('procedures:', fullRecipe.procedures);
  console.log('instructions_en:', fullRecipe.instructions_en);
  console.log('instructions:', fullRecipe.instructions);
  console.log('procedure:', fullRecipe.procedure);

  return (
    <div className="modal-overlay">
      <div className="modal-content w-full max-w-4xl mx-auto my-8 px-4 sm:px-6 lg:px-8 pt-4 pb-4" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className={`flex justify-between items-center mb-6 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <div className={i18n.language === 'ar' ? 'text-right' : 'text-left'}>
            <h2 className="text-2xl font-bold text-card-foreground">{fullRecipe.name_en}</h2>
            <p className="text-muted-foreground mt-1">{fullRecipe.name_ar}</p>
          </div>
          <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <button 
              onClick={handlePrint} 
              className={`inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}
            >
              <Printer className={`h-4 w-4 ${i18n.language === 'ar' ? 'ml-2' : 'mr-2'}`} />
              Print
            </button>
            <button 
              onClick={onClose} 
              className="text-muted-foreground hover:text-card-foreground p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Print Content */}
        <div className="print-content space-y-6">
          {/* Print Header */}
          <div className="hidden print:block text-center mb-6">
            <h1 className="text-2xl font-bold mb-2">{fullRecipe.name_en}</h1>
            <p className="text-gray-600">{fullRecipe.name_ar}</p>
            <div className="border-t border-gray-300 mt-4 pt-4">
              <p className="text-sm text-gray-500">
                Generated on: {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>

          {/* Recipe Overview Card */}
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center mr-4">
                <ChefHat className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-card-foreground">Recipe Overview</h3>
                <p className="text-sm text-muted-foreground">Basic information and specifications</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Recipe Information */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-muted/50 rounded-lg p-4 border border-border">
                  <h4 className="text-sm font-medium text-card-foreground mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Basic Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground w-24">English:</span>
                      <span className="text-sm font-medium text-card-foreground">{fullRecipe.name_en}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground w-24">Arabic:</span>
                      <span className="text-sm font-medium text-card-foreground">{fullRecipe.name_ar}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground w-24">Category:</span>
                      <span className="text-sm font-medium text-card-foreground">{getCategoryName(fullRecipe.category_id)}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground w-24">Servings:</span>
                      <span className="text-sm font-medium text-card-foreground">{fullRecipe.servings}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground w-24">Portion Size:</span>
                      <span className="text-sm font-medium text-card-foreground">
                        {fullRecipe.portion_size} {fullRecipe.unit}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground w-24">Holding Temp:</span>
                      <span className="text-sm font-medium text-card-foreground">
                        {fullRecipe.holding_temperature ? `${fullRecipe.holding_temperature}°C` : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Nutritional Information */}
                <div className="bg-chart-3/10 rounded-lg p-4 border border-chart-3/20">
                  <h4 className="text-sm font-medium text-chart-3 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-chart-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    Nutritional Information
                  </h4>
                  <div className="space-y-4">
                    {/* Allergens */}
                    <div className="flex items-start">
                      <span className="text-sm text-chart-3 w-24 mt-1">Allergens:</span>
                      <div className="flex-1">
                        {fullRecipe.computed_allergens && fullRecipe.computed_allergens.length > 0 ? (
                          <AllergenBadge 
                            allergens={fullRecipe.computed_allergens} 
                            variant="warning" 
                            size="xs"
                            maxDisplay={5}
                          />
                        ) : (
                          <span className="text-sm font-medium text-chart-3">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>


          {/* Cost Breakdown Card */}
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-chart-3/20 rounded-lg flex items-center justify-center mr-4">
                <DollarSign className="w-5 h-5 text-chart-3" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-card-foreground">Cost Breakdown</h3>
                <p className="text-sm text-muted-foreground">Financial analysis and pricing</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-chart-3/10 rounded-lg p-4 text-center border border-chart-3/20">
                <div className="text-xs font-medium text-chart-3 mb-2">Total Cost</div>
                <div className="text-xl font-bold text-chart-3">{formatCurrency(totalCost)}</div>
              </div>
              <div className="bg-primary/10 rounded-lg p-4 text-center border border-primary/20">
                <div className="text-xs font-medium text-primary mb-2">Total Cost per Serving</div>
                <div className="text-xl font-bold text-primary">{formatCurrency(costPerServing)}</div>
                </div>
            </div>
          </div>


          {/* Ingredients Card */}
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-chart-2/20 rounded-lg flex items-center justify-center mr-4">
                <Package className="w-5 h-5 text-chart-2" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-card-foreground">Ingredients</h3>
                <p className="text-sm text-muted-foreground">Material and recipe ingredients breakdown</p>
              </div>
            </div>

          {fullRecipe.ingredients && fullRecipe.ingredients.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                        {t('recipes.code')}
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                        {t('common.name')}
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                        {t('recipes.type')}
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                        {t('recipes.quantityUsed')}
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                        {t('common.unit')}
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                        Waste %
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                        {t('common.cost')}
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                        % of Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {fullRecipe.ingredients.map((ingredient, index) => {
                      const isMaterial = ingredient.material_id;
                      const name = isMaterial ? ingredient.material_name_en : ingredient.recipe_name_en;
                      const quantity = parseFloat(ingredient.quantity || 0);
                      const wastePercentage = parseFloat(ingredient.waste_percentage || 0);
                      const effectiveQuantity = quantity * (1 + wastePercentage / 100);
                      
                      const cost = isMaterial ? 
                        (ingredient.material_unit_cost || 0) * effectiveQuantity :
                        (ingredient.recipe_cost_per_serving || 0) * effectiveQuantity;
                      const percentage = totalCost > 0 ? ((cost / totalCost) * 100) : 0;
                      
                      return (
                        <tr key={ingredient.id || index} className="hover:bg-muted/50">
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-mono text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                            {ingredient.material_id || ingredient.recipe_ingredient_id}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                            {name || 'Unknown'}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              isMaterial ? 'bg-chart-1/10 text-chart-1' : 'bg-chart-3/10 text-chart-3'
                            }`}>
                              {isMaterial ? t('recipes.rawMaterial') : t('recipes.recipe')}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                            {ingredient.quantity}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                            {isMaterial 
                              ? (ingredient.material_sub_unit || 'piece')
                              : (ingredient.recipe_unit || 'serving')
                            }
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                            {wastePercentage > 0 ? `${wastePercentage}%` : '-'}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                            {formatCurrency(cost)}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                            {formatPercentage(percentage)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
            </div>
          ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No ingredients found for this recipe.</p>
            </div>
          )}
            </div>

          {/* Procedure Card */}
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-card-foreground">Procedure</h3>
                <p className="text-sm text-muted-foreground">Detailed cooking procedure and instructions</p>
            </div>
          </div>

            <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
              <div className="prose prose-sm max-w-none">
                {fullRecipe.procedures && fullRecipe.procedures.length > 0 ? (
                  <div className="space-y-3">
                    {fullRecipe.procedures.map((procedure, index) => (
                      <div key={procedure.id || index} className="border-l-4 border-primary pl-4">
                        <div className="flex items-start space-x-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-primary text-white text-xs font-bold rounded-full flex-shrink-0">
                            {procedure.step_number}
                          </span>
                          <div className="flex-1">
                            <p className="text-sm text-card-foreground leading-relaxed">
                              {procedure.instruction_en}
                            </p>
                            {procedure.image_url && (
                              <div className="mt-2">
                                <img
                                  src={procedure.image_url}
                                  alt={`Procedure step ${procedure.step_number}`}
                                  className="w-20 h-20 object-cover rounded-lg border border-primary/30 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => {
                                    setModalImageUrl(procedure.image_url);
                                    setImageModalOpen(true);
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-card-foreground leading-relaxed">
                    {fullRecipe.instructions_en || fullRecipe.instructions || fullRecipe.procedure || 'No procedure available for this recipe.'}
                  </p>
                )}
          </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {imageModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-card-foreground">Step Image</h3>
              <button
                type="button"
                onClick={() => {
                  setImageModalOpen(false);
                  setModalImageUrl('');
                }}
                className="text-muted-foreground hover:text-card-foreground transition-colors"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <img
                src={modalImageUrl}
                alt="Step image"
                className="max-w-full max-h-[70vh] object-contain mx-auto"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeDetails; 